import React, { useState, useEffect, useCallback } from 'react';
import './AdminStudentRegistration.css';
import AdminModal from '../AdminModal';
import { isSuperAdmin, isUserManager, getBranchId, fetchWithAuth } from '../../utils/auth';
import { API_URL } from '../../config';
import {
  listAcademicYears, listGrades, listRegistrations, createStudent, updateStudent,
  bookIssuedStatus, volunteerSignupStatus, BOOK_STATUS_LABELS, VOLUNTEER_STATUS_LABELS, STATUS_BADGE_CLASS,
} from './studentRegistrationApi';

const centsToDollars = (cents) => `$${((cents || 0) / 100).toFixed(2)}`;

// Quotes a CSV field only when it actually needs it (contains a comma,
// quote, or newline), doubling any internal quotes per the CSV spec.
const csvEscape = (value) => {
  const str = String(value ?? '');
  return /[",\n]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
};

const BLANK_STUDENT = {
  id: '', first_name: '', last_name: '', phone: '', primary_email: '', secondary_email: '',
  mother_name: '', father_name: '', date_of_birth: '', notes: '',
};

const STUDENT_FIELDS = [
  { key: 'first_name', label: 'First name', required: true },
  { key: 'last_name', label: 'Last name', required: true },
  { key: 'phone', label: 'Phone' },
  { key: 'primary_email', label: 'Primary email' },
  { key: 'secondary_email', label: 'Secondary email' },
  { key: 'mother_name', label: 'Parent 1' },
  { key: 'father_name', label: 'Parent 2' },
  { key: 'date_of_birth', label: 'Date of birth', type: 'date' },
  { key: 'notes', label: 'Notes', wide: true },
];

// Add/Edit Student, as a dialog over the roster -- shared by both flows.
const StudentModal = ({ title, badge, values, onChange, onSave, onCancel, saving, saveLabel, error, showId }) => (
  <AdminModal
    title={title} badge={badge} onClose={onCancel} onSubmit={onSave}
    saving={saving} saveLabel={saveLabel} error={error} size="lg"
  >
    <div className="admin-modal-grid">
      {showId && (
        <label className="admin-modal-field">
          <span>Student ID *</span>
          <input value={values.id} onChange={e => onChange('id', e.target.value)} autoFocus />
        </label>
      )}
      {STUDENT_FIELDS.map((f, i) => (
        <label key={f.key} className={`admin-modal-field ${f.wide ? 'admin-modal-wide' : ''}`}>
          <span>{f.label}{f.required ? ' *' : ''}</span>
          <input
            type={f.type || 'text'}
            value={values[f.key]}
            onChange={e => onChange(f.key, e.target.value)}
            autoFocus={!showId && i === 0}
          />
        </label>
      ))}
    </div>
  </AdminModal>
);

const AdminStudentRegistrationRoster = () => {
  const superAdmin = isSuperAdmin();
  // Registrar/Evaluator-type roles only ever work the current year's
  // roster — no picker, always locked to current. Admins can still browse
  // prior years' rosters (e.g. to check historical numbers).
  const canPickYear = isUserManager();
  // Creating/editing a Student's identity record is admin-only on the
  // backend (POST/PUT /students), same as the old "+ Add New Student"
  // shortcut used to be on the Check-In page — it just lives here now.
  const canManageStudents = isUserManager();
  const [branches, setBranches] = useState([]);
  const [branchId, setBranchId] = useState(getBranchId() || 1);

  const [years, setYears] = useState([]);
  const [currentYear, setCurrentYear] = useState(null);
  const [yearId, setYearId] = useState(null);
  // Full active-grade list, not grades-offered-this-year -- a registration
  // can exist under any grade regardless of whether Setup ever configured
  // an "offering" (that's only used to auto-populate book lists), so tying
  // this filter to offerings left it empty whenever Setup hadn't been done.
  const [grades, setGrades] = useState([]);
  const [gradeFilter, setGradeFilter] = useState('');
  const [paymentFilter, setPaymentFilter] = useState('');
  const [bookFilter, setBookFilter] = useState('');

  const [registrations, setRegistrations] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [bookBreakdown, setBookBreakdown] = useState({ issued: 0, partial: 0, not_issued: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const [showAddForm, setShowAddForm] = useState(false);
  const [newStudent, setNewStudent] = useState(BLANK_STUDENT);
  const [addingStudent, setAddingStudent] = useState(false);

  const [editingStudentId, setEditingStudentId] = useState(null);
  const [editStudent, setEditStudent] = useState(BLANK_STUDENT);
  const [savingEdit, setSavingEdit] = useState(false);
  // The banner confirming a save is at the top of the page, out of sight if the admin edited a row
  // far down a long roster -- so the saved row itself briefly highlights where they're looking.
  const [flashStudentId, setFlashStudentId] = useState(null);

  useEffect(() => {
    if (superAdmin) {
      fetchWithAuth(`${API_URL}/api/admin/branches`)
        .then(r => r.json())
        .then(d => setBranches(d.branches || []))
        .catch(() => {});
    }
  }, [superAdmin]);

  useEffect(() => {
    listAcademicYears().then(({ ok, data }) => {
      if (ok) {
        setYears(data.academic_years || []);
        const current = (data.academic_years || []).find(y => y.is_current) || data.academic_years?.[0] || null;
        setCurrentYear(current);
        setYearId(current ? current.id : null);
      }
    });
    listGrades(true).then(({ ok, data }) => { if (ok) setGrades(data.grades || []); });
  }, []);

  const load = useCallback(async () => {
    if (!yearId) return;
    setLoading(true);
    setError('');
    // payment_status/book_status are filtered server-side now (not with a
    // client-side .filter() over whatever was already loaded), so
    // total_count/book_status_breakdown are always authoritative for
    // whatever filter combination is currently selected.
    const { ok, data } = await listRegistrations(yearId, gradeFilter || null, branchId, paymentFilter || null, bookFilter || null);
    if (ok) {
      setRegistrations(data.registrations || []);
      setTotalCount(data.total_count ?? (data.registrations || []).length);
      setBookBreakdown(data.book_status_breakdown || { issued: 0, partial: 0, not_issued: 0 });
    } else {
      setError(data.error || 'Failed to load roster.');
    }
    setLoading(false);
  }, [yearId, gradeFilter, branchId, paymentFilter, bookFilter]);

  useEffect(() => { load(); }, [load]);

  // Already filtered server-side -- kept as its own name so the JSX below
  // (which pre-dates this change) doesn't need to be touched.
  const visible = registrations;

  // Exports exactly what's currently on screen -- same server-filtered
  // rows the table renders and the summary line counts, so the file always
  // matches whatever grade/payment/book filters are active. Built client-
  // side since the data's already loaded; no extra backend round trip.
  const handleDownloadCsv = () => {
    const header = [
      'Student ID', 'First Name', 'Last Name', 'Grade', 'Section', 'Phone', 'Primary Email',
      'Fee Charged', 'Amount Paid', 'Payment Status', 'Book Status', 'Volunteering Status', 'Teacher Email',
    ];
    const rows = visible.map(r => [
      r.student?.id, r.student?.first_name, r.student?.last_name, r.grade_name, r.section || '',
      r.student?.phone || '', r.student?.primary_email || '',
      centsToDollars(r.registration_fee_charged_cents), centsToDollars(r.amount_paid_cents),
      r.payment_status, BOOK_STATUS_LABELS[bookIssuedStatus(r)] || '', VOLUNTEER_STATUS_LABELS[volunteerSignupStatus(r)],
      r.teacher_email || '',
    ]);
    const csv = [header, ...rows].map(row => row.map(csvEscape).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    const yearLabel = years.find(y => y.id === yearId)?.year || 'roster';
    link.href = url;
    link.download = `roster_${yearLabel}_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleAddStudent = async () => {
    if (!newStudent.id.trim() || !newStudent.first_name.trim() || !newStudent.last_name.trim()) {
      setError('Student ID, first name, and last name are required.');
      return;
    }
    setAddingStudent(true);
    setError('');
    const { ok, data } = await createStudent(newStudent);
    if (ok) {
      setShowAddForm(false);
      setNewStudent(BLANK_STUDENT);
      setMessage(`Added ${data.student.first_name} ${data.student.last_name} (ID: ${data.student.id}). `
        + 'Go to Registration Updates to search for them and complete this year\'s registration.');
    } else {
      setError(data.error || 'Failed to add student.');
    }
    setAddingStudent(false);
  };

  const cancelAddStudent = () => {
    setShowAddForm(false);
    setNewStudent(BLANK_STUDENT);
    setError('');
  };

  const startEditStudent = (student) => {
    setEditingStudentId(student.id);
    setEditStudent({
      id: student.id,
      first_name: student.first_name || '',
      last_name: student.last_name || '',
      phone: student.phone || '',
      primary_email: student.primary_email || '',
      secondary_email: student.secondary_email || '',
      mother_name: student.mother_name || '',
      father_name: student.father_name || '',
      date_of_birth: student.date_of_birth || '',
      notes: student.notes || '',
    });
    setError('');
    setMessage('');
  };

  const cancelEditStudent = () => {
    setEditingStudentId(null);
    setEditStudent(BLANK_STUDENT);
    setError('');
  };

  const saveEditStudent = async () => {
    if (!editStudent.first_name.trim() || !editStudent.last_name.trim()) {
      setError('First name and last name are required.');
      return;
    }
    setSavingEdit(true);
    setError('');
    // Only send fields that actually have a value. A blank optional field
    // here almost always means the browser's <input type="date"> silently
    // couldn't display an existing non-ISO-format value (or the field was
    // simply never touched) -- never an intentional clear -- so sending it
    // as "" would silently wipe out real, already-saved data.
    const payload = { first_name: editStudent.first_name.trim(), last_name: editStudent.last_name.trim() };
    for (const field of ['phone', 'primary_email', 'secondary_email', 'mother_name', 'father_name', 'date_of_birth', 'notes']) {
      if (editStudent[field] && editStudent[field].trim()) payload[field] = editStudent[field].trim();
    }
    const { ok, data } = await updateStudent(editingStudentId, payload);
    if (ok) {
      // Patch the edited student's info into every visible row for them,
      // without a full reload.
      setRegistrations(prev => prev.map(r =>
        r.student?.id === editingStudentId ? { ...r, student: data.student } : r
      ));
      setMessage('Student updated.');
      setFlashStudentId(editingStudentId);
      setTimeout(() => setFlashStudentId(null), 2500);
      cancelEditStudent();
    } else {
      setError(data.error || 'Failed to update student.');
    }
    setSavingEdit(false);
  };

  return (
    <div className="faq-manager-container">
      <div className="faq-manager-header">
        <div>
          <h2>Roster</h2>
          <p className="faq-manager-subtitle">
            {!canPickYear && currentYear ? `${currentYear.year} (current)` : ''} registrations - who's registered and who still owes a balance.
          </p>
        </div>
        <div className="faq-manager-header-right">
          {superAdmin && branches.length > 0 && (
            <select value={branchId} onChange={e => setBranchId(Number(e.target.value))} className="branch-select">
              {branches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
          )}
          {canPickYear && (
            <select value={yearId || ''} onChange={e => setYearId(Number(e.target.value))} className="branch-select">
              {years.map(y => <option key={y.id} value={y.id}>{y.year}{y.is_current ? ' (current)' : ''}</option>)}
            </select>
          )}
          <select value={gradeFilter} onChange={e => setGradeFilter(e.target.value)} className="branch-select">
            <option value="">All grades</option>
            {grades.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
          </select>
          <select value={paymentFilter} onChange={e => setPaymentFilter(e.target.value)} className="branch-select">
            <option value="">All payment statuses</option>
            <option value="paid">Paid</option>
            <option value="partial">Partial</option>
            <option value="pending">Pending</option>
          </select>
          <select value={bookFilter} onChange={e => setBookFilter(e.target.value)} className="branch-select">
            <option value="">All book statuses</option>
            <option value="issued">{BOOK_STATUS_LABELS.issued}</option>
            <option value="partial">{BOOK_STATUS_LABELS.partial}</option>
            <option value="not_issued">{BOOK_STATUS_LABELS.not_issued}</option>
          </select>
          <button className="btn-secondary" onClick={handleDownloadCsv} disabled={visible.length === 0}>
            Download CSV
          </button>
          {canManageStudents && (
            <button className="btn-secondary" onClick={() => { setError(''); setMessage(''); setShowAddForm(true); }}>+ Add New Student</button>
          )}
        </div>
      </div>

      {error && <div className="error-banner">{error}</div>}
      {message && <div className="sr-success-banner">{message}</div>}

      {!loading && (
        <div className="sr-roster-summary">
          <strong>{totalCount}</strong> {totalCount === 1 ? 'student' : 'students'}
          {(gradeFilter || paymentFilter || bookFilter) ? ' matching filters' : ''}
          {' — Books: '}
          {bookBreakdown.issued} {BOOK_STATUS_LABELS.issued} · {bookBreakdown.partial} {BOOK_STATUS_LABELS.partial} · {bookBreakdown.not_issued} {BOOK_STATUS_LABELS.not_issued}
        </div>
      )}

      {showAddForm && canManageStudents && (
        <StudentModal
          title="Add New Student"
          values={newStudent}
          onChange={(k, v) => setNewStudent(st => ({ ...st, [k]: v }))}
          onSave={handleAddStudent}
          onCancel={cancelAddStudent}
          saving={addingStudent}
          saveLabel="Save Student"
          error={error}
          showId
        />
      )}

      {editingStudentId && (
        <StudentModal
          title="Edit Student"
          badge={`(ID: ${editingStudentId})`}
          values={editStudent}
          onChange={(k, v) => setEditStudent(st => ({ ...st, [k]: v }))}
          onSave={saveEditStudent}
          onCancel={cancelEditStudent}
          saving={savingEdit}
          saveLabel="Save Changes"
          error={error}
        />
      )}

      {loading ? <p style={{ padding: '20px', color: '#64748b' }}>Loading…</p> : (
        <div className="sr-table-scroll">
        <table className="admin-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Student</th>
              <th>Grade</th>
              <th>Section</th>
              <th>Enrollment</th>
              <th>Fee Charged</th>
              <th>Amount Paid</th>
              <th>Status</th>
              <th>Books</th>
              <th>Volunteering</th>
              <th>Teacher</th>
            </tr>
          </thead>
          <tbody>
            {visible.length === 0 ? (
              <tr><td colSpan={11} style={{ textAlign: 'center', color: '#94a3b8', padding: '32px' }}>No registrations match.</td></tr>
            ) : (
              visible.map(r => (
                <tr key={r.id} className={`${canManageStudents ? 'sr-row-clickable' : ''} ${r.student?.id === flashStudentId ? 'sr-row-flash' : ''}`}
                    onClick={canManageStudents ? () => startEditStudent(r.student) : undefined}>
                  <td>{r.student?.id}</td>
                  <td className={canManageStudents ? 'sr-name-link' : ''}>{r.student?.first_name} {r.student?.last_name}</td>
                  <td>{r.grade_name}</td>
                  <td>{r.section || '—'}</td>
                  <td>
                    <span className={`sr-payment-badge ${r.is_active ? 'sr-status-paid' : 'sr-status-pending'}`}>
                      {r.is_active ? 'Active' : 'Withdrawn'}
                    </span>
                  </td>
                  <td>{centsToDollars(r.registration_fee_charged_cents)}</td>
                  <td>{centsToDollars(r.amount_paid_cents)}</td>
                  <td><span className={`sr-payment-badge ${STATUS_BADGE_CLASS[r.payment_status]}`}>{r.payment_status}</span></td>
                  <td>
                    {(() => {
                      const status = bookIssuedStatus(r);
                      return status
                        ? <span className={`sr-payment-badge ${STATUS_BADGE_CLASS[status]}`}>{BOOK_STATUS_LABELS[status]}</span>
                        : '—';
                    })()}
                  </td>
                  <td>
                    {(() => {
                      const status = volunteerSignupStatus(r);
                      return <span className={`sr-payment-badge ${STATUS_BADGE_CLASS[status]}`}>{VOLUNTEER_STATUS_LABELS[status]}</span>;
                    })()}
                  </td>
                  <td>{r.teacher_email || '—'}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
        </div>
      )}
    </div>
  );
};

export default AdminStudentRegistrationRoster;
